import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import MessageAPI from "../services/NavaService";

export default function MessagesAdmin() {
    const [tab, setTab] = useState("pending");
    const [pending, setPending] = useState([]);
    const [acknowledged, setAcknowledged] = useState([]);
    const [loading, setLoading] = useState(false);
    const isPendingTab = tab === "pending";
    const isAcknowledgedTab = tab === "acknowledged";

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentMessage, setCurrentMessage] = useState(null);
    const [ackValue, setAckValue] = useState("N");

    useEffect(() => {
        loadAll();
    }, []);

    const customAlert = (text, icon = "info") => {
        Swal.fire({
            text,
            icon,
            didOpen: () => {
                const container = document.querySelector(".swal2-container");
                const popup = document.querySelector(".swal2-popup");
                if (container) container.style.zIndex = "10010";
                if (popup) popup.style.zIndex = "10011";
            },
        });
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [pendRes, doneRes] = await Promise.all([
                MessageAPI.getPendingMessages(),
                MessageAPI.getDoneMessages(),
            ]);
            setPending(Array.isArray(pendRes.data) ? pendRes.data : []);
            setAcknowledged(Array.isArray(doneRes.data) ? doneRes.data : []);
        } catch (err) {
            console.error(err);
            customAlert("Failed to load messages", "error");
            setPending([]);
            setAcknowledged([]);
        } finally {
            setLoading(false);
            setPage(1);
        }
    };

    const currentList = (tab === "pending" ? pending : acknowledged) || [];

    const filtered = currentList.filter((m) => {
        const q = (search || "").trim().toLowerCase();
        if (!q) return true;
        const firstName = (m.firstName || m.first_name || "").toString().toLowerCase();
        const email = (m.email || "").toString().toLowerCase();
        const subject = (m.subject || "").toString().toLowerCase();
        const message = (m.message || "").toString().toLowerCase();
        return firstName.includes(q) || email.includes(q) || subject.includes(q) || message.includes(q);
    });

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

    const openViewModal = async (msg) => {
        setIsModalOpen(true);
        setCurrentMessage(null);
        setAckValue("N");

        const msgId = msg.msgId ?? msg.id ?? msg.msg_id;
        if (!msgId) {
            setCurrentMessage(msg);
            setAckValue((msg.deliveredYn || msg.delivered_yn || "N") === "Y" ? "Y" : "N");
            return;
        }

        try {
            const res = await MessageAPI.getMessageById(msgId);
            const data = res.data;
            setCurrentMessage(data);
            setAckValue((data?.deliveredYn || data?.delivered_yn || data?.deliveredYn === "Y") ? (data.deliveredYn === "Y" ? "Y" : "N") : (data?.delivered_yn === "Y" ? "Y" : "N"));
        } catch (err) {
            console.warn("Failed to fetch message by id; using local copy", err);
            setCurrentMessage(msg);
            setAckValue((msg.deliveredYn || msg.delivered_yn || "N") === "Y" ? "Y" : "N");
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentMessage(null);
        setAckValue("N");
    };

    const handleAcknowledge = async () => {
        if (!currentMessage) return;

        const confirm = await Swal.fire({
            title: "Acknowledge message?",
            text: "Are you sure you want to acknowledge this request?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, save",
            cancelButtonText: "Cancel",
            didOpen: () => {
                const container = document.querySelector(".swal2-container");
                const popup = document.querySelector(".swal2-popup");
                if (container) container.style.zIndex = "10010";
                if (popup) popup.style.zIndex = "10011";
            },
        });

        if (!confirm.isConfirmed) return;

        try {
            const msgId = currentMessage.msgId ?? currentMessage.id ?? currentMessage.msg_id;
            if (!msgId) throw new Error("Message id missing");

            const payload = {
                firstName: currentMessage.firstName ?? currentMessage.first_name ?? "",
                email: currentMessage.email ?? "",
                subject: currentMessage.subject ?? "",
                message: currentMessage.message ?? "",
                deliveredYn: ackValue === "Y" ? "Y" : "N",
            };

            await MessageAPI.updateMessages(msgId, payload);

            customAlert("Message acknowledged", "success");
            closeModal();
            await loadAll();
        } catch (err) {
            console.error(err);
            customAlert("Failed to save message", "error");
        }
    };

    const formatDate = (iso) => {
        try {
            if (!iso) return "";
            const d = new Date(iso);
            if (isNaN(d)) return iso;
            return d.toLocaleString();
        } catch (e) {
            return iso;
        }
    };

    return (
        <>
            <TopNavbar />
            <PageWrapper style={{ marginTop: "5%" }}>
                <TopRow>
                    <h2>Messages</h2>
                    <div className="controls">
                        <TabBtn active={tab === "pending"} onClick={() => { setTab("pending"); setSearch(""); setPage(1); }}>
                            Pending
                        </TabBtn>
                        <TabBtn active={tab === "acknowledged"} onClick={() => { setTab("acknowledged"); setSearch(""); setPage(1); }}>
                            Acknowledged
                        </TabBtn>

                        <input
                            type="text"
                            placeholder="Search name, email, subject or message..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                        <OutlineBtn onClick={() => { setSearch(""); setPage(1); }}>Clear</OutlineBtn>
                    </div>
                </TopRow>

                <TableWrap>
                    {loading ? (
                        <div>Loading…</div>
                    ) : (
                        <>
                            <table>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>From</th>
                                        <th>Email</th>
                                        <th>Subject</th>
                                        <th>Message</th>
                                        <th>Created</th>
                                        <th>Ack</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageItems.map((m) => {
                                        const id = m.msgId ?? m.id ?? m.msg_id ?? "-";
                                        const name = m.firstName ?? m.first_name ?? "";
                                        const email = m.email ?? "";
                                        const subject = m.subject ?? "";
                                        const msgSnippet = (m.message || "").slice(0, 60) + ((m.message || "").length > 60 ? "…" : "");
                                        const delivered = (m.deliveredYn ?? m.delivered_yn ?? "").toString() === "Y";
                                        return (
                                            <tr key={id}>
                                                <td>{id}</td>
                                                <td>{name}</td>
                                                <td>{email}</td>
                                                <td>{subject}</td>
                                                <td title={m.message}>{msgSnippet}</td>
                                                <td>{formatDate(m.createdAt ?? m.created_at)}</td>
                                                <td>{delivered ? "Yes" : "No"}</td>
                                                <td>
                                                    <PrimarySmall onClick={() => openViewModal(m)}>View</PrimarySmall>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {pageItems.length === 0 && (
                                        <tr>
                                            <td colSpan="8">No messages found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            <Pagination>
                                <OutlineSmall disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</OutlineSmall>
                                <span>Page {page} / {pageCount}</span>
                                <OutlineSmall disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next</OutlineSmall>
                            </Pagination>
                        </>
                    )}
                </TableWrap>

                {isModalOpen && currentMessage && (
                    <ModalOverlay onClick={closeModal}>
                        <ModalContent onClick={(e) => e.stopPropagation()}>
                            <ModalHeader>
                                <h3>View Message</h3>
                                <button onClick={closeModal}>Close</button>
                            </ModalHeader>

                            <ModalBody>
                                <label>ID</label>
                                <IdBox>{currentMessage.msgId ?? currentMessage.id ?? currentMessage.msg_id}</IdBox>

                                <label>From</label>
                                <input
                                    type="text"
                                    value={currentMessage.firstName ?? currentMessage.first_name ?? ""}
                                    readOnly={isAcknowledgedTab}
                                    onChange={(e) => setCurrentMessage((p) => ({ ...p, firstName: e.target.value }))}
                                />

                                <label>Email</label>
                                <input
                                    type="text"
                                    value={currentMessage.email ?? ""}
                                    readOnly={isAcknowledgedTab}
                                    onChange={(e) => setCurrentMessage((p) => ({ ...p, email: e.target.value }))}
                                />

                                <label>Subject</label>
                                <input
                                    type="text"
                                    value={currentMessage.subject ?? ""}
                                    readOnly={isAcknowledgedTab}
                                    onChange={(e) => setCurrentMessage((p) => ({ ...p, subject: e.target.value }))}
                                />

                                <label>Message</label>
                                <textarea
                                    rows={8}
                                    value={currentMessage.message ?? ""}
                                    readOnly={isAcknowledgedTab}
                                    onChange={(e) => setCurrentMessage((p) => ({ ...p, message: e.target.value }))}
                                    style={{ resize: "vertical", padding: 8, borderRadius: 6 }}
                                />

                                <label>Created</label>
                                <input
                                    type="text"
                                    value={formatDate(currentMessage.createdAt ?? currentMessage.created_at)}
                                    readOnly
                                />

                                <label>Acknowledged</label>
                                <select
                                    value={ackValue}
                                    onChange={(e) => setAckValue(e.target.value)}
                                    disabled={isAcknowledgedTab}
                                >
                                    <option value="N">No</option>
                                    <option value="Y">Acknowledged</option>
                                </select>
                            </ModalBody>

                            <ModalFooter>
                                <OutlineBtn type="button" onClick={closeModal}>Close</OutlineBtn>

                                {isPendingTab && (
                                    <PrimaryBtn
                                        type="button"
                                        onClick={handleAcknowledge}
                                        disabled={ackValue !== "Y"}
                                        title={ackValue !== "Y" ? "Select 'Acknowledged' to enable" : "Acknowledge"}
                                    >
                                        Acknowledge
                                    </PrimaryBtn>
                                )}
                            </ModalFooter>
                        </ModalContent>
                    </ModalOverlay>
                )}


            </PageWrapper>
        </>
    );
}

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1180px;
  margin: 0 auto;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #222;
  h2 { margin: 0 0 12px 0; font-size:20px; font-weight:700; }
  .controls { display:flex; gap:10px; align-items:center; margin-bottom:12px; }
  input[type="text"] { padding:10px 12px; border:1px solid #e7e7f0; border-radius:8px; min-width:260px; }
`;

const TabBtn = styled.button`
  padding:8px 12px;
  border-radius:8px;
  border:1px solid #e6e7ef;
  background: ${(p) => (p.active ? "linear-gradient(180deg,#6a2cff,#5722c9)" : "#fff")};
  color: ${(p) => (p.active ? "#fff" : "#333")};
  cursor:pointer;
  font-weight:700;
  box-shadow: ${(p) => (p.active ? "0 8px 20px rgba(102,51,204,0.12)" : "none")};
`;

const PrimaryBtn = styled.button`
  padding:9px 12px;
  border-radius:8px;
  border:0;
  background: linear-gradient(180deg,#6a2cff,#5a22c9);
  color:#fff;
  cursor:pointer;
  font-weight:600;
  box-shadow: 0 8px 20px rgba(88,34,200,0.12);
`;

const OutlineBtn = styled.button`
  padding:9px 12px;
  border-radius:8px;
  border:1px solid #e9e9f4;
  background:#fff;
  color:#222;
  cursor:pointer;
`;

const TableWrap = styled.div`
  background:#fff;
  border:1px solid #f0f1f6;
  padding:12px;
  border-radius:10px;
  max-height: calc(100vh - 320px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  table {
    width:100%;
    border-collapse:collapse;
    font-size:14px;
    table-layout: fixed;
    min-width:1000px;
  }

  thead th {
    position:sticky;
    top:0;
    background: linear-gradient(180deg,#fff,#fbfbfe);
    z-index: 3;
    padding:12px;
    text-align:left;
    font-weight:700;
    border-bottom:1px solid #f2f4f8;
  }

  th, td {
    padding:10px 12px;
    border-bottom:1px solid #f6f7fb;
    vertical-align: top;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  td[title] { cursor: help; }

  td.actions {
    white-space: nowrap;
    width: 140px;
  }

  tbody tr:hover { background:#fbfcff; }

  @media (max-width:1000px) {
    table { min-width: 820px; }
    th, td { padding:9px; font-size:13px; }
  }
`;

const Pagination = styled.div`
  display:flex;
  justify-content:center;
  align-items:center;
  gap:10px;
  padding:12px 0;
  font-size:14px;
`;

const ModalOverlay = styled.div`
  position:fixed; inset:0; background: rgba(6,8,20,0.48);
  display:flex; align-items:center; justify-content:center; z-index:5000; padding:16px;
`;

const ModalContent = styled.div`
  width:720px; max-width:96%; max-height:92vh; overflow:auto; background:#fff; border-radius:12px; padding:18px;
  box-shadow: 0 30px 80px rgba(18,24,64,0.16);
`;

const ModalHeader = styled.div`
  display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;
  h3 { margin:0; font-size:18px; font-weight:700; }
  button { background:transparent; border:0; color:#666; font-size:15px; cursor:pointer; }
`;

const ModalBody = styled.div`
  display:flex; flex-direction:column; gap:10px;
  label { font-weight:600; font-size:13px; margin-top:8px; color:#222; }
  input[type="text"], select, textarea { padding:10px; border:1px solid #e9e9f2; border-radius:8px; font-size:14px; }
  textarea { min-height:140px; resize:vertical; }
`;

const IdBox = styled.div`
  padding:8px 10px; background:#f7f8fb; border-radius:8px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", monospace;
`;

const ModalFooter = styled.div`
  display:flex; justify-content:flex-end; gap:10px; margin-top:12px;
`;

const PrimarySmall = styled.button`
  padding:7px 10px; border-radius:8px; border:0; background:#6a2cff; color:#fff; cursor:pointer; margin-right:6px; white-space:nowrap;
`;
const OutlineSmall = styled.button`
  padding:7px 10px; border-radius:8px; border:1px solid #e9e9f4; background:#fff; color:#222; cursor:pointer;
`;

const TopRow = styled.div`
  display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; gap:12px;

  h2 { margin:0; font-size:20px; }

  .controls { display:flex; gap:8px; align-items:center; }

  @media (max-width:720px) {
    flex-direction:column; align-items:flex-start;
    .controls { width:100%; display:flex; gap:8px; flex-wrap:wrap; }
  }
`;
